import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ShieldAlert, Plus, ChevronRight, Loader2, Copy, X } from 'lucide-react';
import { tabletopExercisesAPI, TabletopExercise, TabletopExerciseStatus } from '../../services/tabletopService';

// Exercises with no generated outline yet can't be reused — nothing to copy.
const REUSABLE_STATUSES: TabletopExerciseStatus[] = ['generated', 'approved', 'running', 'complete'];

const STATUS_LABEL: Record<TabletopExerciseStatus, string> = {
  draft: 'Draft',
  generating: 'Generating',
  generated: 'Awaiting review',
  approved: 'Approved',
  running: 'Running',
  complete: 'Complete',
};

const STATUS_COLOR: Record<TabletopExerciseStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  generating: 'bg-amber-100 text-amber-700',
  generated: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  running: 'bg-primary/10 text-primary',
  complete: 'bg-slate-800 text-white',
};

const TabletopProjectHome: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState<TabletopExercise[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [showReusePicker, setShowReusePicker] = useState(false);
  const [cloningId, setCloningId] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId) return;
    tabletopExercisesAPI.list(projectId)
      .then(r => setExercises(r.data.exercises))
      .catch(() => setExercises([]))
      .finally(() => setLoading(false));
  }, [projectId]);

  // Reuse an existing exercise's outline (scenario, personas, injects, etc.)
  // as the seed for a new one — skips the five n8n build-pipeline workflows
  // entirely, so it costs no tokens and takes seconds instead of minutes.
  const reuseExercise = async (sourceExerciseId: string) => {
    if (!projectId) return;
    setCloningId(sourceExerciseId);
    try {
      const r = await tabletopExercisesAPI.clone(projectId, sourceExerciseId);
      navigate(`/projects/${projectId}/tabletop/exercises/${r.data.exercise.exerciseId}`);
    } finally {
      setCloningId(null);
    }
  };

  const reusableExercises = (exercises || []).filter(e => REUSABLE_STATUSES.includes(e.status));

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1200px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <ShieldAlert size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">Cyber Tabletop Exercises</h1>
              <p className="text-slate-500 text-sm font-medium">
                AI-blended incident response tabletop simulations
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {reusableExercises.length > 0 && (
              <button
                onClick={() => setShowReusePicker(true)}
                className="flex items-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors"
              >
                <Copy size={16} /> Reuse Outline
              </button>
            )}
            <button
              onClick={() => navigate(`/projects/${projectId}/tabletop/exercises/new`)}
              className="flex items-center gap-2 rounded-xl h-10 px-4 bg-primary text-white text-sm font-bold hover:opacity-90 shadow-sm transition-opacity"
            >
              <Plus size={16} /> New Exercise
            </button>
          </div>
        </header>

        {loading && (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 size={22} className="animate-spin" />
          </div>
        )}

        {!loading && exercises && exercises.length === 0 && (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-10 text-center">
            <p className="text-sm font-semibold text-navy">No exercises yet</p>
            <p className="text-sm text-slate-500 mt-1">
              Start from a one-line seed — e.g. "ransomware hits our case management system via our outsourced IT provider" —
              and build out the full scenario package from there.
            </p>
            <button
              onClick={() => navigate(`/projects/${projectId}/tabletop/exercises/new`)}
              className="mt-4 inline-flex items-center gap-2 rounded-xl h-10 px-4 bg-primary text-white text-sm font-bold hover:opacity-90 transition-opacity"
            >
              <Plus size={16} /> New Exercise
            </button>
          </div>
        )}

        {!loading && exercises && exercises.length > 0 && (
          <div className="flex flex-col gap-3">
            {exercises.map(exercise => (
              <button
                key={exercise.exerciseId}
                onClick={() => navigate(`/projects/${projectId}/tabletop/exercises/${exercise.exerciseId}`)}
                className="flex items-center justify-between gap-4 p-5 bg-white border border-[#e2e8f0] rounded-2xl text-left hover:shadow-md hover:border-primary/30 transition-all group"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${STATUS_COLOR[exercise.status]}`}>
                      {STATUS_LABEL[exercise.status]}
                    </span>
                    {exercise.orgProfile.sector && (
                      <span className="text-[11px] text-slate-400 font-medium">{exercise.orgProfile.sector}</span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-navy truncate">{exercise.seed}</p>
                </div>
                <ChevronRight size={18} className="text-slate-300 group-hover:text-primary transition-colors flex-shrink-0" />
              </button>
            ))}
          </div>
        )}

      </div>

      {showReusePicker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl w-full max-w-[560px] max-h-[80vh] flex flex-col overflow-hidden shadow-xl">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
              <div>
                <h2 className="text-sm font-bold text-navy">Reuse an outline</h2>
                <p className="text-xs text-slate-400 mt-0.5">Copies the scenario, personas and injects into a new exercise — no AI regeneration.</p>
              </div>
              <button onClick={() => setShowReusePicker(false)} className="text-slate-400 hover:text-navy">
                <X size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {reusableExercises.map(exercise => (
                <button
                  key={exercise.exerciseId}
                  onClick={() => reuseExercise(exercise.exerciseId)}
                  disabled={cloningId !== null}
                  className="w-full flex items-center justify-between gap-4 px-6 py-4 text-left hover:bg-slate-50 disabled:opacity-50 transition-colors"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${STATUS_COLOR[exercise.status]}`}>
                        {STATUS_LABEL[exercise.status]}
                      </span>
                      {exercise.orgProfile.sector && (
                        <span className="text-[11px] text-slate-400 font-medium">{exercise.orgProfile.sector}</span>
                      )}
                    </div>
                    <p className="text-sm font-semibold text-navy truncate">{exercise.seed}</p>
                  </div>
                  {cloningId === exercise.exerciseId ? (
                    <Loader2 size={16} className="animate-spin text-primary flex-shrink-0" />
                  ) : (
                    <Copy size={16} className="text-slate-300 flex-shrink-0" />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabletopProjectHome;
